import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserTie,
    faPhone,
    faEnvelope,
    faAddressCard,
    faHandshake,
    faCalendarAlt,
    faFileUpload,
    faRupeeSign,
    faPercent,
    faAlignLeft,
    faStickyNote,
    faSave,
    faTimes,
    faCheckCircle,
    faExclamationTriangle,
    faExchangeAlt,
    faSpinner,
    faChevronDown,
    faBuilding,
    faStore,
    faUser,
    faIdCard,
    faClock,
    faFileContract,
    faMoneyBillWave,
    faChartLine,
    faInfoCircle,
    faSearch,
    faPlus,
    faEdit,
    faTrash,
    faMoneyCheckDollar
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// TOAST NOTIFICATION COMPONENT
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            case 'info':
                return faCheckCircle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            case 'info':
                return 'Info';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// SHOP SELECTOR MODAL COMPONENT
// ==============================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="ozui_modal-overlay" onClick={onClose}>
            <div className="ozui_modal" onClick={e => e.stopPropagation()}>
                <div className="ozui_modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozui_modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozui_modal-body">
                    {loading ? (
                        <div className="ozui_loading-state">{translations.loadingShops || 'Loading shops...'}</div>
                    ) : shops.length === 0 ? (
                        <div className="ozui_empty-state">{translations.noShops || 'No shops available'}</div>
                    ) : (
                        shops.map((shop) => (
                            <div
                                key={shop.shop_id}
                                className={`ozui_shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozui_active' : ''}`}
                                onClick={() => onSelectShop(shop)}
                            >
                                <div className="ozui_shop-info">
                                    <div className="ozui_shop-name">{shop.shop_name}</div>
                                    <div className="ozui_shop-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                </div>
                                {currentShop?.shop_id === shop.shop_id && (
                                    <div className="ozui_shop-badge">✓ {translations.current || 'Current'}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

// ==============================================
// TRANSLATION FUNCTION
// ==============================================
const getTranslations = async (lang) => {
    const defaultTexts = {
        addPartner: 'Add Partner',
        editPartner: 'Edit Partner',
        fillDetails: 'Fill in the partner details below',
        partnerDetails: 'Partner Details',
        partnerName: 'Partner Name',
        phone: 'Phone Number',
        email: 'Email Address',
        address: 'Address',
        partnershipType: 'Partnership Type',
        partnershipDuration: 'Partnership Duration',
        partnershipContract: 'Partnership Contract',
        investmentAmount: 'Investment Amount',
        profitPercentage: 'Profit %',
        description: 'Description',
        notes: 'Notes / Remarks',
        contractStartDate: 'Contract Start Date',
        contractEndDate: 'Contract End Date',
        status: 'Status',
        active: 'Active',
        inactive: 'Inactive',
        suspended: 'Suspended',
        terminated: 'Terminated',
        savePartner: 'Save Partner',
        updatePartner: 'Update Partner',
        clearButton: 'Clear',
        adding: 'Adding...',
        updating: 'Updating...',
        partnerSaved: 'Partner saved successfully!',
        partnerUpdated: 'Partner updated successfully!',
        failedAdd: 'Failed to save partner',
        failedUpdate: 'Failed to update partner',
        networkError: 'Network error occurred',
        nameRequired: 'Partner name is required',
        nameTooShort: 'Name must be at least 2 characters',
        invalidEmail: 'Please enter a valid email address',
        invalidInvestment: 'Investment amount cannot be negative',
        invalidProfit: 'Profit percentage must be between 0 and 100',
        contractEndBeforeStart: 'Contract end date cannot be before start date',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        currentShop: 'Current Shop',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        optional: 'Optional',
        required: 'Required',
        allType: 'All Type Partnership',
        productBased: 'Product Based Partnership',
        uploadContract: 'Upload Contract',
        chooseFile: 'Choose File',
        noFileSelected: 'No file selected',
        fileUploadSuccess: 'Contract uploaded successfully',
        fileUploadFailed: 'Failed to upload contract',
        selectType: 'Select Partnership Type',
        selectDuration: 'Select Duration',
        enterPartnerName: 'Enter partner name',
        enterPhone: 'Enter phone number',
        enterEmail: 'Enter email address',
        enterAddress: 'Enter address',
        enterDescription: 'Enter description',
        enterNotes: 'Enter notes / remarks',
        enterInvestment: 'Enter investment amount',
        enterProfit: 'Enter profit percentage',
        // Product selection translations
        selectProducts: 'Select Products',
        selectedProducts: 'Selected Products',
        selected: 'selected',
        searchProducts: 'Search products...',
        loadingProducts: 'Loading products...',
        noProductsAvailable: 'No products available. Please add products first.',
        noProductsFound: 'No products found matching your search.',
        clearAll: 'Clear All',
        productName: 'Product Name',
        productBatch: 'Batch',
        productQty: 'Qty',
        // Expenses/Salaries Included - Dropdown
        expensesSalariesIncluded: 'Expenses/Salaries Included',
        includeExpenses: 'Include Expenses & Salary',
        doNotIncludeExpenses: 'Do not include Expenses & Salary',
        selectExpensesOption: 'Select option...'
    };

    if (lang === 'ur') {
        return {
            ...defaultTexts,
            addPartner: 'پارٹنر شامل کریں',
            editPartner: 'پارٹنر میں ترمیم کریں',
            fillDetails: 'براہ کرم پارٹنر کی تفصیلات درج کریں',
            partnerDetails: 'پارٹنر کی تفصیلات',
            partnerName: 'پارٹنر کا نام',
            phone: 'فون نمبر',
            email: 'ای میل ایڈریس',
            address: 'پتہ',
            partnershipType: 'شراکت کی قسم',
            partnershipDuration: 'شراکت کی مدت',
            partnershipContract: 'شراکت کا معاہدہ',
            investmentAmount: 'سرمایہ کاری کی رقم',
            profitPercentage: 'منافع کا فیصد',
            description: 'تفصیل',
            notes: 'نوٹس / ریمارکس',
            contractStartDate: 'معاہدے کی آغاز کی تاریخ',
            contractEndDate: 'معاہدے کی اختتامی تاریخ',
            status: 'حالت',
            active: 'فعال',
            inactive: 'غیر فعال',
            suspended: 'معطل',
            terminated: 'ختم شدہ',
            savePartner: 'پارٹنر محفوظ کریں',
            updatePartner: 'پارٹنر اپ ڈیٹ کریں',
            clearButton: 'صاف کریں',
            adding: 'شامل ہو رہا ہے...',
            updating: 'اپ ڈیٹ ہو رہا ہے...',
            partnerSaved: 'پارٹنر کامیابی سے محفوظ ہو گیا!',
            partnerUpdated: 'پارٹنر کامیابی سے اپ ڈیٹ ہو گیا!',
            failedAdd: 'پارٹنر محفوظ کرنے میں ناکامی',
            failedUpdate: 'پارٹنر اپ ڈیٹ کرنے میں ناکامی',
            networkError: 'نیٹ ورک کی خرابی',
            nameRequired: 'پارٹنر کا نام ضروری ہے',
            nameTooShort: 'نام کم از کم 2 حروف کا ہونا چاہیے',
            invalidEmail: 'براہ کرم درست ای میل ایڈریس درج کریں',
            invalidInvestment: 'سرمایہ کاری کی رقم منفی نہیں ہو سکتی',
            invalidProfit: 'منافع کا فیصد 0 اور 100 کے درمیان ہونا چاہیے',
            contractEndBeforeStart: 'معاہدے کی اختتامی تاریخ آغاز کی تاریخ سے پہلے نہیں ہو سکتی',
            success: 'کامیابی',
            error: 'خرابی',
            warning: 'انتباہ',
            selectShop: 'پہلے دکان منتخب کریں',
            currentShop: 'موجودہ دکان',
            noShopSelected: 'کوئی دکان منتخب نہیں۔ براہ کرم پہلے دکان تبدیل کریں',
            switchShop: 'دکان تبدیل کریں',
            shopSwitched: 'دکان کامیابی سے تبدیل ہو گئی',
            shopSwitchFailed: 'دکان تبدیل کرنے میں ناکامی',
            loadingShops: 'دکانیں لوڈ ہو رہی ہیں...',
            noShops: 'کوئی دکان دستیاب نہیں',
            current: 'موجودہ',
            optional: 'اختیاری',
            required: 'ضروری',
            allType: 'تمام قسم کی شراکت',
            productBased: 'مصنوعات کی بنیاد پر شراکت',
            uploadContract: 'معاہدہ اپ لوڈ کریں',
            chooseFile: 'فائل منتخب کریں',
            noFileSelected: 'کوئی فائل منتخب نہیں',
            fileUploadSuccess: 'معاہدہ کامیابی سے اپ لوڈ ہو گیا',
            fileUploadFailed: 'معاہدہ اپ لوڈ کرنے میں ناکامی',
            selectType: 'شراکت کی قسم منتخب کریں',
            selectDuration: 'مدت منتخب کریں',
            enterPartnerName: 'پارٹنر کا نام درج کریں',
            enterPhone: 'فون نمبر درج کریں',
            enterEmail: 'ای میل ایڈریس درج کریں',
            enterAddress: 'پتہ درج کریں',
            enterDescription: 'تفصیل درج کریں',
            enterNotes: 'نوٹس / ریمارکس درج کریں',
            enterInvestment: 'سرمایہ کاری کی رقم درج کریں',
            enterProfit: 'منافع کا فیصد درج کریں',
            selectProducts: 'مصنوعات منتخب کریں',
            selectedProducts: 'منتخب شدہ مصنوعات',
            selected: 'منتخب',
            searchProducts: 'مصنوعات تلاش کریں...',
            loadingProducts: 'مصنوعات لوڈ ہو رہی ہیں...',
            noProductsAvailable: 'کوئی مصنوعات دستیاب نہیں۔ براہ کرم پہلے مصنوعات شامل کریں۔',
            noProductsFound: 'آپ کی تلاش سے مماثل کوئی مصنوعات نہیں ملی۔',
            clearAll: 'سب صاف کریں',
            productName: 'مصنوعات کا نام',
            productBatch: 'بیچ',
            productQty: 'مقدار',
            expensesSalariesIncluded: 'اخراجات/تنخواہیں شامل ہیں',
            includeExpenses: 'اخراجات اور تنخواہیں شامل کریں',
            doNotIncludeExpenses: 'اخراجات اور تنخواہیں شامل نہ کریں',
            selectExpensesOption: 'آپشن منتخب کریں...'
        };
    }

    return defaultTexts;
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const SolarPartners = ({ partnerId = null, onSuccess = null }) => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();

    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return user?.language || localStorage.getItem('ourzest_language') || 'en';
    });

    // Auto-focus ref for Partner Name field
    const partnerNameRef = useRef(null);

    // ==============================================
    // STATE DECLARATIONS
    // ==============================================

    // Form state
    const [formData, setFormData] = useState({
        partner_name: '',
        phone: '',
        email: '',
        address: '',
        partnership_type: 'ALL',
        partnership_duration: '',
        partnership_contract: null,
        investment_amount: '',
        profit_percentage: '',
        description: '',
        notes: '',
        contract_start_date: '',
        contract_end_date: '',
        status: 'active',
        expenses_salaries_included: false,
        themeColor: localStorage.getItem('ourzest_theme') || '#6366f1'
    });

    // Expenses/Salaries Included Options
    const expensesOptions = [
        { value: true, label: 'Include Expenses & Salary' },
        { value: false, label: 'Do not include Expenses & Salary' }
    ];

    // Partnership Type Options
    const partnershipTypeOptions = [
        { value: 'ALL', label: 'All Type Partnership' },
        { value: 'Product Based', label: 'Product Based Partnership' }
    ];

    // Partnership Duration Options
    const durationOptions = [
        { value: '', label: 'Select Duration' },
        { value: '3_months', label: '3 Months' },
        { value: '6_months', label: '6 Months' },
        { value: '1_year', label: '1 Year' },
        { value: '2_years', label: '2 Years' },
        { value: '3_years', label: '3 Years' },
        { value: '5_years', label: '5 Years' },
        { value: 'lifetime', label: 'Lifetime' },
        { value: 'custom', label: 'Custom Duration' }
    ];

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [translations, setTranslations] = useState({});
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });

    // Manual dropdown states
    const [showPartnershipTypeDropdown, setShowPartnershipTypeDropdown] = useState(false);
    const [partnershipTypeSearchTerm, setPartnershipTypeSearchTerm] = useState('');
    const [showDurationDropdown, setShowDurationDropdown] = useState(false);
    const [durationSearchTerm, setDurationSearchTerm] = useState('');
    
    // Expenses dropdown states
    const [showExpensesDropdown, setShowExpensesDropdown] = useState(false);
    const [expensesSearchTerm, setExpensesSearchTerm] = useState('');

    // Refs for dropdowns
    const partnershipTypeDropdownRef = useRef(null);
    const durationDropdownRef = useRef(null);
    const expensesDropdownRef = useRef(null);

    // File upload state
    const [contractFile, setContractFile] = useState(null);
    const [contractFileName, setContractFileName] = useState('');
    const fileInputRef = useRef(null);

    // ==============================================
    // PRODUCT SELECTION STATE
    // ==============================================
    const [availableProducts, setAvailableProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const productDropdownRef = useRef(null);
    const [loadingProductDetails, setLoadingProductDetails] = useState(false);

    // Refs for preventing duplicate submissions
    const isSubmittingRef = useRef(false);
    const toastTimerRef = useRef(null);
    const lastSubmitTimeRef = useRef(0);
    const lastPartnerDataRef = useRef('');
    const isInitialLoadRef = useRef(true);
    const isClearingRef = useRef(false);

    // ==============================================
    // FILTERED OPTIONS
    // ==============================================
    const filteredPartnershipTypes = partnershipTypeSearchTerm.trim() === ''
        ? partnershipTypeOptions
        : partnershipTypeOptions.filter(type =>
            type.label.toLowerCase().includes(partnershipTypeSearchTerm.toLowerCase())
        );

    const filteredDurations = durationSearchTerm.trim() === ''
        ? durationOptions
        : durationOptions.filter(d =>
            d.value !== '' && d.label.toLowerCase().includes(durationSearchTerm.toLowerCase())
        );

    const filteredExpenses = expensesSearchTerm.trim() === ''
        ? expensesOptions
        : expensesOptions.filter(option =>
            option.label.toLowerCase().includes(expensesSearchTerm.toLowerCase())
        );

    const filteredProducts = productSearchTerm.trim() === ''
        ? availableProducts
        : availableProducts.filter(product =>
            product.product_name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
            (product.batch_no && product.batch_no.toLowerCase().includes(productSearchTerm.toLowerCase())) ||
            (product.barcode && product.barcode.toLowerCase().includes(productSearchTerm.toLowerCase())) ||
            (product.category && product.category.toLowerCase().includes(productSearchTerm.toLowerCase()))
        );

    // ==============================================
    // SHOW TOAST FUNCTION
    // ==============================================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    // ==============================================
    // PRODUCT FETCHING
    // ==============================================
    const fetchProducts = useCallback(async () => {
        if (!currentShop) return;
        
        setLoadingProducts(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user?.username || '',
                    shop_id: currentShop.shop_id
                })
            });

            const responseText = await response.text();
            
            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error:', responseText);
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success') {
                setAvailableProducts(data.data || []);
            } else {
                showToast('error', data.message || 'Failed to load products');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showToast('error', 'Failed to load products');
        } finally {
            setLoadingProducts(false);
        }
    }, [currentShop, user?.username, showToast]);

    // Fetch products when partnership type changes to Product Based
    useEffect(() => {
        if (formData.partnership_type === 'Product Based' && currentShop) {
            fetchProducts();
        } else {
            setAvailableProducts([]);
            setSelectedProducts([]);
            setProductSearchTerm('');
        }
    }, [formData.partnership_type, currentShop, fetchProducts]);

    // ==============================================
    // PRODUCT SELECTION HANDLERS
    // ==============================================
    const toggleProductSelection = (product) => {
        setSelectedProducts(prev => {
            const exists = prev.some(p => p.id === product.id);
            if (exists) {
                return prev.filter(p => p.id !== product.id);
            } else {
                return [...prev, product];
            }
        });
    };

    const toggleSelectAllProducts = () => {
        if (selectedProducts.length === filteredProducts.length && filteredProducts.length > 0) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts([...filteredProducts]);
        }
    };

    const removeProduct = (productId) => {
        setSelectedProducts(prev => prev.filter(p => p.id !== productId));
    };

    const clearSelectedProducts = () => {
        setSelectedProducts([]);
    };

    // ==============================================
    // SELECT HANDLERS
    // ==============================================
    const selectPartnershipType = (type) => {
        setFormData(prev => ({ ...prev, partnership_type: type.value }));
        setPartnershipTypeSearchTerm(type.label);
        setShowPartnershipTypeDropdown(false);
    };

    const selectDuration = (duration) => {
        setFormData(prev => ({ ...prev, partnership_duration: duration.value }));
        setDurationSearchTerm(duration.label);
        setShowDurationDropdown(false);
    };

    const selectExpensesOption = (option) => {
        setFormData(prev => ({ ...prev, expenses_salaries_included: option.value }));
        setExpensesSearchTerm(option.label);
        setShowExpensesDropdown(false);
        if (errors.expenses_salaries_included) {
            setErrors(prev => ({ ...prev, expenses_salaries_included: '' }));
        }
    };

    // ==============================================
    // INPUT CHANGE HANDLERS
    // ==============================================
    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === 'file') {
            if (files && files[0]) {
                setContractFile(files[0]);
                setContractFileName(files[0].name);
                setFormData(prev => ({
                    ...prev,
                    partnership_contract: files[0].name
                }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
            if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // ==============================================
    // HANDLE FILE UPLOAD
    // ==============================================
    const handleFileUpload = async (file) => {
        if (!file) return null;

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('action', 'upload_contract');
            formData.append('username', user?.username || '');
            formData.append('shop_id', currentShop?.shop_id || '');

            const response = await fetch('https://ourzestapp.online/oz_backend/solar_module/solar_partners.php', {
                method: 'POST',
                body: formData
            });

            const responseText = await response.text();
            
            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error. Raw response:', responseText.substring(0, 500));
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success') {
                return data.file_path;
            } else {
                showToast('error', data.message || 'Failed to upload contract');
                return null;
            }
        } catch (error) {
            console.error('File upload error:', error);
            showToast('error', 'Network error while uploading file');
            return null;
        }
    };

    // ==============================================
    // VALIDATE FORM
    // ==============================================
    const validateForm = () => {
        const newErrors = {};

        if (!formData.partner_name?.trim()) {
            newErrors.partner_name = translations.nameRequired || 'Partner name is required';
        } else if (formData.partner_name.trim().length < 2) {
            newErrors.partner_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }

        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = translations.invalidEmail || 'Please enter a valid email address';
        }

        if (formData.investment_amount && parseFloat(formData.investment_amount) < 0) {
            newErrors.investment_amount = translations.invalidInvestment || 'Investment amount cannot be negative';
        }

        if (formData.profit_percentage && (parseFloat(formData.profit_percentage) < 0 || parseFloat(formData.profit_percentage) > 100)) {
            newErrors.profit_percentage = translations.invalidProfit || 'Profit percentage must be between 0 and 100';
        }

        if (formData.contract_start_date && formData.contract_end_date) {
            const start = new Date(formData.contract_start_date);
            const end = new Date(formData.contract_end_date);
            if (end < start) {
                newErrors.contract_end_date = translations.contractEndBeforeStart || 'Contract end date cannot be before start date';
            }
        }

        // Validate product selection for Product Based partnerships
        if (formData.partnership_type === 'Product Based' && selectedProducts.length === 0) {
            newErrors.products = 'Please select at least one product for product-based partnership';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ==============================================
    // HANDLE SUBMIT
    // ==============================================
    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        if (!validateForm()) return;

        if (isSubmittingRef.current) {
            showToast('warning', 'Please wait, submission in progress...');
            return;
        }

        const now = Date.now();
        if (now - lastSubmitTimeRef.current < 3000) {
            showToast('warning', 'Please wait before submitting again');
            return;
        }

        const currentPartnerData = JSON.stringify({
            partner_name: formData.partner_name,
            phone: formData.phone,
            email: formData.email,
            shop_id: currentShop.shop_id
        });

        if (!isEditMode && currentPartnerData === lastPartnerDataRef.current) {
            showToast('warning', 'This partner appears to be a duplicate. Please check.');
            return;
        }

        lastPartnerDataRef.current = currentPartnerData;
        lastSubmitTimeRef.current = now;

        isSubmittingRef.current = true;
        setLoading(true);

        const submitButton = document.querySelector('button[type="submit"]');
        if (submitButton) submitButton.disabled = true;

        try {
            const currentUsername = user?.username || 'Unknown';

            // Handle file upload first if there's a file
            let contractPath = formData.partnership_contract;
            if (contractFile) {
                const uploadedPath = await handleFileUpload(contractFile);
                if (uploadedPath) {
                    contractPath = uploadedPath;
                } else {
                    showToast('warning', 'Contract upload failed, but partner will be saved');
                }
            }

            // Prepare selected product IDs
            const selectedProductIds = selectedProducts.map(p => p.id);

            const partnerData = {
                action: isEditMode ? 'update' : 'add',
                partner_name: formData.partner_name,
                phone: formData.phone || null,
                email: formData.email || null,
                address: formData.address || null,
                partnership_type: formData.partnership_type || 'ALL',
                partnership_duration: formData.partnership_duration || null,
                partnership_contract: contractPath || null,
                investment_amount: formData.investment_amount || 0,
                profit_percentage: formData.profit_percentage || 0,
                description: formData.description || null,
                notes: formData.notes || null,
                contract_start_date: formData.contract_start_date || null,
                contract_end_date: formData.contract_end_date || null,
                status: formData.status || 'active',
                expenses_salaries_included: formData.expenses_salaries_included || false,
                username: currentUsername,
                shop_id: currentShop.shop_id,
                shop_name: currentShop.shop_name || null,
                shop_code: currentShop.shop_code || null,
                added_by_user_id: user?.id || null,
                added_by_username: currentUsername,
                selected_products: selectedProductIds,
                product_selection_mode: 'multiple',
                _timestamp: Date.now()
            };

            if (isEditMode && partnerId) {
                partnerData.partner_id = partnerId;
            }

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch('https://ourzestapp.online/oz_backend/solar_module/solar_partners.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(partnerData),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Server error response:', errorText);
                throw new Error(`Server responded with status ${response.status}: ${errorText.substring(0, 200)}`);
            }

            const responseText = await response.text();

            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error. Raw response:', responseText.substring(0, 500));
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success') {
                showToast('success', isEditMode
                    ? (translations.partnerUpdated || 'Partner updated successfully!')
                    : (translations.partnerSaved || 'Partner saved successfully!'));

                if (!isEditMode) {
                    setFormData({
                        partner_name: '',
                        phone: '',
                        email: '',
                        address: '',
                        partnership_type: 'ALL',
                        partnership_duration: '',
                        partnership_contract: null,
                        investment_amount: '',
                        profit_percentage: '',
                        description: '',
                        notes: '',
                        contract_start_date: '',
                        contract_end_date: '',
                        status: 'active',
                        expenses_salaries_included: false,
                        themeColor: formData.themeColor
                    });
                    setPartnershipTypeSearchTerm('');
                    setDurationSearchTerm('');
                    setExpensesSearchTerm('');
                    setContractFile(null);
                    setContractFileName('');
                    setSelectedProducts([]);
                    setProductSearchTerm('');
                    setErrors({});
                    lastPartnerDataRef.current = '';

                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }

                    setTimeout(() => {
                        if (partnerNameRef.current) {
                            partnerNameRef.current.focus();
                        }
                    }, 100);
                }

                if (onSuccess) onSuccess(data.partner_id || partnerId);
            } else {
                showToast('error', data.message || (isEditMode
                    ? 'Failed to update partner'
                    : 'Failed to save partner'));
                lastPartnerDataRef.current = '';
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                showToast('error', 'Request timeout. Please try again.');
            } else {
                console.error('Submit error:', error);
                showToast('error', error.message || translations.networkError || 'Network error occurred');
            }
            lastPartnerDataRef.current = '';
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                setLoading(false);
                if (submitButton) submitButton.disabled = false;
            }, 3000);
        }
    };

    // ==============================================
    // HANDLE CLEAR
    // ==============================================
    const handleClear = () => {
        isClearingRef.current = true;

        setFormData({
            partner_name: '',
            phone: '',
            email: '',
            address: '',
            partnership_type: 'ALL',
            partnership_duration: '',
            partnership_contract: null,
            investment_amount: '',
            profit_percentage: '',
            description: '',
            notes: '',
            contract_start_date: '',
            contract_end_date: '',
            status: 'active',
            expenses_salaries_included: false,
            themeColor: formData.themeColor
        });
        setPartnershipTypeSearchTerm('');
        setDurationSearchTerm('');
        setExpensesSearchTerm('');
        setContractFile(null);
        setContractFileName('');
        setSelectedProducts([]);
        setProductSearchTerm('');
        setErrors({});
        setIsEditMode(false);
        lastPartnerDataRef.current = '';

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        showToast('success', 'Form cleared');

        setTimeout(() => {
            if (partnerNameRef.current) {
                partnerNameRef.current.focus();
            }
        }, 100);

        setTimeout(() => {
            isClearingRef.current = false;
        }, 500);
    };

    // ==============================================
    // HANDLE SWITCH SHOP
    // ==============================================
    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                setPartnershipTypeSearchTerm('');
                setDurationSearchTerm('');
                setExpensesSearchTerm('');
                setContractFile(null);
                setContractFileName('');
                setSelectedProducts([]);
                setProductSearchTerm('');
                setAvailableProducts([]);
                setFormData(prev => ({
                    ...prev,
                    partner_name: '',
                    phone: '',
                    email: '',
                    address: '',
                    partnership_type: 'ALL',
                    partnership_duration: '',
                    partnership_contract: null,
                    investment_amount: '',
                    profit_percentage: '',
                    description: '',
                    notes: '',
                    contract_start_date: '',
                    contract_end_date: '',
                    status: 'active',
                    expenses_salaries_included: false
                }));
            }
            showToast(success ? 'success' : 'error',
                success ? (translations.shopSwitched || 'Shop switched successfully')
                    : (translations.shopSwitchFailed || 'Failed to switch shop'));
        } catch (error) {
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // ==============================================
    // LOAD PARTNER DATA
    // ==============================================
    const loadPartnerData = async (id) => {
        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/solar_module/solar_partners.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get',
                    partner_id: id,
                    username: user?.username,
                    shop_id: currentShop.shop_id
                })
            });

            const responseText = await response.text();

            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error:', responseText);
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success' && data.partner) {
                const partner = data.partner;
                setFormData(prev => ({
                    ...prev,
                    partner_name: partner.partner_name || '',
                    phone: partner.phone || '',
                    email: partner.email || '',
                    address: partner.address || '',
                    partnership_type: partner.partnership_type || 'ALL',
                    partnership_duration: partner.partnership_duration || '',
                    partnership_contract: partner.partnership_contract || null,
                    investment_amount: partner.investment_amount || '',
                    profit_percentage: partner.profit_percentage || '',
                    description: partner.description || '',
                    notes: partner.notes || '',
                    contract_start_date: partner.contract_start_date || '',
                    contract_end_date: partner.contract_end_date || '',
                    status: partner.status || 'active',
                    expenses_salaries_included: partner.expenses_salaries_included == 1
                }));

                const selectedType = partnershipTypeOptions.find(t => t.value === partner.partnership_type);
                setPartnershipTypeSearchTerm(selectedType ? selectedType.label : '');

                const selectedDuration = durationOptions.find(d => d.value === partner.partnership_duration);
                setDurationSearchTerm(selectedDuration ? selectedDuration.label : '');

                // Set expenses dropdown value
                const selectedExpenses = expensesOptions.find(e => e.value === (partner.expenses_salaries_included == 1));
                setExpensesSearchTerm(selectedExpenses ? selectedExpenses.label : '');

                if (partner.partnership_contract) {
                    setContractFileName(partner.partnership_contract);
                }

                // Load selected products for product-based partnerships
                if (partner.partnership_type === 'Product Based' && partner.selected_products) {
                    const productIds = partner.selected_products.split(',').map(id => parseInt(id.trim()));
                    const validIds = productIds.filter(id => !isNaN(id) && id > 0);
                    
                    if (validIds.length > 0) {
                        // Fetch product details
                        setLoadingProductDetails(true);
                        try {
                            const productResponse = await fetch('https://ourzestapp.online/oz_backend/solar_module/solar_partners.php', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                    action: 'get_product_details',
                                    product_ids: validIds,
                                    username: user?.username,
                                    shop_id: currentShop.shop_id
                                })
                            });

                            const productText = await productResponse.text();
                            let productData;
                            try {
                                productData = JSON.parse(productText);
                            } catch (e) {
                                console.error('Product parse error:', productText);
                                productData = { status: 'error' };
                            }

                            if (productData.status === 'success' && productData.data) {
                                setSelectedProducts(productData.data);
                            } else {
                                // Try to load from partner.product_details if available
                                if (partner.product_details && partner.product_details.length > 0) {
                                    setSelectedProducts(partner.product_details);
                                } else {
                                    // Fallback: just store IDs
                                    setSelectedProducts(validIds.map(id => ({ id, product_name: `Product #${id}` })));
                                }
                            }
                        } catch (err) {
                            console.error('Error loading product details:', err);
                            // Fallback
                            setSelectedProducts(validIds.map(id => ({ id, product_name: `Product #${id}` })));
                        } finally {
                            setLoadingProductDetails(false);
                        }
                    }
                }
            } else {
                showToast('error', data.message || 'Failed to load partner data');
            }
        } catch (error) {
            console.error('Load partner error:', error);
            showToast('error', translations.networkError || 'Network error');
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // EFFECTS
    // ==============================================

    // Click outside handlers for dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (partnershipTypeDropdownRef.current && !partnershipTypeDropdownRef.current.contains(event.target)) {
                setShowPartnershipTypeDropdown(false);
            }
            if (durationDropdownRef.current && !durationDropdownRef.current.contains(event.target)) {
                setShowDurationDropdown(false);
            }
            if (expensesDropdownRef.current && !expensesDropdownRef.current.contains(event.target)) {
                setShowExpensesDropdown(false);
            }
            if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
                setShowProductDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Tab key handler
    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowPartnershipTypeDropdown(false);
                setShowDurationDropdown(false);
                setShowExpensesDropdown(false);
                setShowProductDropdown(false);
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    // Auto-focus
    useEffect(() => {
        setTimeout(() => {
            if (partnerNameRef.current) {
                partnerNameRef.current.focus();
            }
        }, 100);
    }, []);

    // Cleanup
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // Language sync
    useEffect(() => {
        if (user?.language && user.language !== currentLanguage) {
            setCurrentLanguage(user.language);
        }
    }, [user?.language]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Load partner if editing
    useEffect(() => {
        if (partnerId) {
            setIsEditMode(true);
            loadPartnerData(partnerId);
        }
    }, [partnerId, currentShop]);

    // Load shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
            } finally {
                setLoadingShops(false);
            }
        };
        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    // ==============================================
    // THEME AND DARK MODE EFFECTS
    // ==============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);

        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99; g = 102; b = 241;
        }

        let style = document.getElementById('dynamic-orb-styles-partners');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles-partners';
            document.head.appendChild(style);
        }

        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;

        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentThemeColor) {
                applyTheme(storedTheme);
            }
        }, 500);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, currentThemeColor]);

    // Dark mode
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);

            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }

            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };

        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);

                const container = document.querySelector('.ozui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozui-dark');
                    } else {
                        container.classList.remove('ozui-dark');
                    }
                }

                if (isDark) {
                    document.body.classList.add('ozui-dark');
                    document.documentElement.classList.add('ozui-dark');
                } else {
                    document.body.classList.remove('ozui-dark');
                    document.documentElement.classList.remove('ozui-dark');
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // RTL
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="ozui-shop-indicator no-shop">
                    <div className="ozui-shop-badge">
                        <span className="ozui-shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                </div>
            );
        }

        return (
            <div className="ozui-shop-indicator">
                <div className="ozui-shop-badge">
                    <FontAwesomeIcon icon={faStore} />
                    <span className="ozui-shop-name">{currentShop.shop_name}</span>
                    {currentShop.shop_code && (
                        <span className="ozui-shop-code">({currentShop.shop_code})</span>
                    )}
                </div>
                <button
                    className="ozui-header-shop-btn"
                    onClick={() => setShowShopSelector(true)}
                >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span>{translations.switchShop || 'Switch Shop'}</span>
                </button>
            </div>
        );
    };

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Modern Animated Sticky Header */}
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>
                                {isEditMode ? (translations.editPartner || 'Edit Partner') : (translations.addPartner || 'Add Partner')}
                            </h1>
                            <p>{translations.fillDetails || 'Fill in the partner details below'}</p>
                        </div>
                    </div>

                    <div className="ozui-header-right">
                        {renderShopIndicator()}
                        <div className="ozui-header-actions">
                            <button
                                className="ozui-action-btn clear-draft"
                                onClick={handleClear}
                                disabled={loading}
                                title={translations.clearButton || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>{translations.clearButton || 'Clear'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    {/* Partner Name */}
                    <div className={`ozui-field ${errors.partner_name ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={partnerNameRef}
                                type="text"
                                name="partner_name"
                                id="partner_name"
                                value={formData.partner_name}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="partner_name">
                                <FontAwesomeIcon icon={faUserTie} />
                                {translations.partnerName || 'Partner Name'} <span>*</span>
                            </label>
                        </div>
                        {errors.partner_name && <small>{errors.partner_name}</small>}
                    </div>

                    {/* Phone Number */}
                    <div className={`ozui-field ${errors.phone ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="tel"
                                name="phone"
                                id="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="phone">
                                <FontAwesomeIcon icon={faPhone} />
                                {translations.phone || 'Phone Number'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.phone && <small>{errors.phone}</small>}
                    </div>

                    {/* Email */}
                    <div className={`ozui-field ${errors.email ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="email">
                                <FontAwesomeIcon icon={faEnvelope} />
                                {translations.email || 'Email Address'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.email && <small>{errors.email}</small>}
                    </div>

                    {/* Address */}
                    <div className={`ozui-field ${errors.address ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                name="address"
                                id="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                rows="2"
                            />
                            <label htmlFor="address">
                                <FontAwesomeIcon icon={faAddressCard} />
                                {translations.address || 'Address'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.address && <small>{errors.address}</small>}
                    </div>

                    {/* Partnership Type */}
                    <div className={`ozui-field ${errors.partnership_type ? 'ozui-error' : ''}`} ref={partnershipTypeDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="partnership_type"
                                value={partnershipTypeSearchTerm}
                                onChange={(e) => {
                                    setPartnershipTypeSearchTerm(e.target.value);
                                    setShowPartnershipTypeDropdown(true);
                                    if (formData.partnership_type && !partnershipTypeOptions.find(t =>
                                        t.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, partnership_type: '' }));
                                    }
                                }}
                                onFocus={() => setShowPartnershipTypeDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="partnership_type">
                                <FontAwesomeIcon icon={faHandshake} />
                                {translations.partnershipType || 'Partnership Type'} <span>*</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.partnership_type && <small>{errors.partnership_type}</small>}
                        {showPartnershipTypeDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredPartnershipTypes.length > 0 ? (
                                    filteredPartnershipTypes.map(type => (
                                        <div
                                            key={type.value}
                                            className={`ozui-category-option ${formData.partnership_type === type.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectPartnershipType(type)}
                                        >
                                            {type.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No types found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Partnership Duration */}
                    <div className={`ozui-field ${errors.partnership_duration ? 'ozui-error' : ''}`} ref={durationDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="partnership_duration"
                                value={durationSearchTerm}
                                onChange={(e) => {
                                    setDurationSearchTerm(e.target.value);
                                    setShowDurationDropdown(true);
                                    if (formData.partnership_duration && !durationOptions.find(d =>
                                        d.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, partnership_duration: '' }));
                                    }
                                }}
                                onFocus={() => setShowDurationDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="partnership_duration">
                                <FontAwesomeIcon icon={faClock} />
                                {translations.partnershipDuration || 'Partnership Duration'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.partnership_duration && <small>{errors.partnership_duration}</small>}
                        {showDurationDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredDurations.length > 0 ? (
                                    filteredDurations.map(duration => (
                                        <div
                                            key={duration.value}
                                            className={`ozui-category-option ${formData.partnership_duration === duration.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectDuration(duration)}
                                        >
                                            {duration.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No durations found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Contract Start Date */}
                    <div className={`ozui-field ${errors.contract_start_date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="contract_start_date"
                                id="contract_start_date"
                                value={formData.contract_start_date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="contract_start_date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {translations.contractStartDate || 'Contract Start'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.contract_start_date && <small>{errors.contract_start_date}</small>}
                    </div>

                    {/* Contract End Date */}
                    <div className={`ozui-field ${errors.contract_end_date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="contract_end_date"
                                id="contract_end_date"
                                value={formData.contract_end_date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="contract_end_date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {translations.contractEndDate || 'Contract End'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.contract_end_date && <small>{errors.contract_end_date}</small>}
                    </div>

                    {/* Partnership Contract - File Upload */}
                    <div className={`ozui-field ${errors.partnership_contract ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper ozui-file-wrapper">
                            <input
                                type="file"
                                name="partnership_contract"
                                id="partnership_contract"
                                ref={fileInputRef}
                                onChange={handleChange}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                disabled={loading}
                            />
                            <label htmlFor="partnership_contract">
                                <FontAwesomeIcon icon={faFileContract} />
                                {translations.partnershipContract || 'Partnership Contract'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            {contractFileName && (
                                <span className="ozui-file-name">
                                    <FontAwesomeIcon icon={faCheckCircle} className="ozui-file-check" />
                                    {contractFileName}
                                </span>
                            )}
                        </div>
                        {errors.partnership_contract && <small>{errors.partnership_contract}</small>}
                    </div>
                </div>

                <div className="ozui-form-row">
                    {/* Investment Amount */}
                    <div className={`ozui-field ${errors.investment_amount ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="investment_amount"
                                id="investment_amount"
                                value={formData.investment_amount}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="investment_amount">
                                <FontAwesomeIcon icon={faMoneyBillWave} />
                                {translations.investmentAmount || 'Investment Amount'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.investment_amount && <small>{errors.investment_amount}</small>}
                    </div>

                    {/* Profit Percentage */}
                    <div className={`ozui-field ${errors.profit_percentage ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="profit_percentage"
                                id="profit_percentage"
                                value={formData.profit_percentage}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                max="100"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="profit_percentage">
                                <FontAwesomeIcon icon={faPercent} />
                                {translations.profitPercentage || 'Profit %'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.profit_percentage && <small>{errors.profit_percentage}</small>}
                    </div>

                    {/* Status */}
                    <div className={`ozui-field ${errors.status ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <select
                                name="status"
                                id="status"
                                value={formData.status}
                                onChange={handleChange}
                                disabled={loading}
                            >
                                <option value="active">{translations.active || 'Active'}</option>
                                <option value="inactive">{translations.inactive || 'Inactive'}</option>
                                <option value="suspended">{translations.suspended || 'Suspended'}</option>
                                <option value="terminated">{translations.terminated || 'Terminated'}</option>
                            </select>
                            <label htmlFor="status">
                                <FontAwesomeIcon icon={faIdCard} />
                                {translations.status || 'Status'}
                            </label>
                        </div>
                        {errors.status && <small>{errors.status}</small>}
                    </div>
                </div>

                {/* ============================================== */}
                {/* EXPENSES/SALARIES INCLUDED - CUSTOM DROPDOWN */}
                {/* ============================================== */}
                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.expenses_salaries_included ? 'ozui-error' : ''}`} ref={expensesDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="expenses_salaries_included"
                                value={expensesSearchTerm}
                                onChange={(e) => {
                                    setExpensesSearchTerm(e.target.value);
                                    setShowExpensesDropdown(true);
                                    if (formData.expenses_salaries_included !== undefined && !expensesOptions.find(opt =>
                                        opt.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, expenses_salaries_included: false }));
                                    }
                                }}
                                onFocus={() => setShowExpensesDropdown(true)}
                                placeholder={translations.selectEpensesOption || ''}
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="expenses_salaries_included">
                                <FontAwesomeIcon icon={faMoneyCheckDollar} />
                                {translations.expensesSalariesIncluded || 'Expenses/Salaries Included'} <span>*</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.expenses_salaries_included && <small>{errors.expenses_salaries_included}</small>}
                        {showExpensesDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredExpenses.length > 0 ? (
                                    filteredExpenses.map(option => (
                                        <div
                                            key={option.value ? 'true' : 'false'}
                                            className={`ozui-category-option ${formData.expenses_salaries_included === option.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectExpensesOption(option)}
                                        >
                                          
                                            {option.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No options found</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ============================================== */}
                {/* PRODUCT SELECTION - ONLY FOR PRODUCT BASED */}
                {/* ============================================== */}
                {formData.partnership_type === 'Product Based' && (
                    <div className="ozui-product-selection-section">
                        <div className="ozui-section-header">
                            <h4>
                                <FontAwesomeIcon icon={faStore} />
                                {translations.selectProducts || 'Select Products'}
                            </h4>
                            <span className="ozui-selected-count">
                                {selectedProducts.length} {translations.selected || 'selected'}
                            </span>
                        </div>

                        {/* Product Search Dropdown */}
                        <div className={`ozui-field ${errors.products ? 'ozui-error' : ''}`} ref={productDropdownRef}>
                            <div className="ozui-input-wrapper ozui-category-wrapper">
                                <input
                                    type="text"
                                    value={productSearchTerm}
                                    onChange={(e) => {
                                        setProductSearchTerm(e.target.value);
                                        setShowProductDropdown(true);
                                    }}
                                    onFocus={() => setShowProductDropdown(true)}
                                    placeholder={translations.searchProducts || 'Search products...'}
                                    disabled={loading || loadingProducts}
                                    autoComplete="off"
                                />
                                <label>
                                    <FontAwesomeIcon icon={faSearch} />
                                    {translations.searchProducts || 'Search Products'}
                                    <span>({translations.optional || 'Optional'})</span>
                                </label>
                                {loadingProducts && <FontAwesomeIcon icon={faSpinner} spin className="ozui-loading-icon" />}
                                <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                            </div>
                            
                            {showProductDropdown && (
                                <div className="ozui-category-dropdown ozui-product-dropdown">
                                    {loadingProducts ? (
                                        <div className="ozui-dropdown-loading">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            {translations.loadingProducts || 'Loading products...'}
                                        </div>
                                    ) : filteredProducts.length > 0 ? (
                                        <>
                                            <div className="ozui-dropdown-header">
                                                <button 
                                                    className="ozui-select-all-btn"
                                                    onClick={toggleSelectAllProducts}
                                                    type="button"
                                                >
                                                    {selectedProducts.length === filteredProducts.length && filteredProducts.length > 0 
                                                        ? 'Deselect All' 
                                                        : 'Select All'}
                                                </button>
                                                <span className="ozui-product-count">
                                                    {filteredProducts.length} products
                                                </span>
                                            </div>
                                            <div className="ozui-product-list">
                                                {filteredProducts.map(product => (
                                                    <div
                                                        key={product.id}
                                                        className={`ozui-product-option ${selectedProducts.some(p => p.id === product.id) ? 'ozui-selected' : ''}`}
                                                        onClick={() => toggleProductSelection(product)}
                                                    >
                                                        <div className="ozui-product-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedProducts.some(p => p.id === product.id)}
                                                                onChange={() => toggleProductSelection(product)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </div>
                                                        <div className="ozui-product-info">
                                                            <span className="ozui-product-name">{product.product_name}</span>
                                                            {product.batch_no && (
                                                                <span className="ozui-product-batch">{translations.productBatch || 'Batch'}: {product.batch_no}</span>
                                                            )}
                                                            {product.quantity !== undefined && (
                                                                <span className="ozui-product-qty">{translations.productQty || 'Qty'}: {product.quantity}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="ozui-dropdown-empty">
                                            {availableProducts.length === 0 
                                                ? (translations.noProductsAvailable || 'No products available. Please add products first.')
                                                : (translations.noProductsFound || 'No products found matching your search.')}
                                        </div>
                                    )}
                                </div>
                            )}
                            {errors.products && <small>{errors.products}</small>}
                        </div>

                        {/* Selected Products Tags */}
                        {selectedProducts.length > 0 && (
                            <div className="ozui-selected-products">
                                <div className="ozui-selected-header">
                                    <span className="ozui-selected-title">
                                        {translations.selectedProducts || 'Selected Products:'}
                                    </span>
                                    <button 
                                        className="ozui-clear-selected-btn"
                                        onClick={clearSelectedProducts}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                        {translations.clearAll || 'Clear All'}
                                    </button>
                                </div>
                                <div className="ozui-product-tags">
                                    {selectedProducts.map(product => (
                                        <div key={product.id} className="ozui-product-tag">
                                            <span className="ozui-tag-text">{product.product_name}</span>
                                            {product.batch_no && (
                                                <span className="ozui-tag-batch">{product.batch_no}</span>
                                            )}
                                            <button 
                                                className="ozui-tag-remove"
                                                onClick={() => removeProduct(product.id)}
                                                type="button"
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="ozui-form-row">
                    {/* Description */}
                    <div className={`ozui-field ${errors.description ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                name="description"
                                id="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                rows="3"
                            />
                            <label htmlFor="description">
                                <FontAwesomeIcon icon={faAlignLeft} />
                                {translations.description || 'Description'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.description && <small>{errors.description}</small>}
                    </div>

                    {/* Notes */}
                    <div className={`ozui-field ${errors.notes ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                name="notes"
                                id="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                rows="3"
                            />
                            <label htmlFor="notes">
                                <FontAwesomeIcon icon={faStickyNote} />
                                {translations.notes || 'Notes / Remarks'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.notes && <small>{errors.notes}</small>}
                    </div>
                </div>

                {/* Form Actions */}
                <div className="ozui-form-actions">
                    <button
                        type="submit"
                        className="ozui-btn ozui-btn-primary"
                        disabled={loading || !currentShop || isSubmittingRef.current}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{isEditMode ? (translations.updating || 'Updating...') : (translations.adding || 'Adding...')}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (translations.updatePartner || 'Update Partner') : (translations.savePartner || 'Save Partner')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SolarPartners;